<template>
  <v-dialog v-model="dialogVisible" max-width="600px" persistent content-class="rec-dialog">
    <v-card class="rec-card" dark>
      <div class="rec-frame-tl"></div>
      <div class="rec-frame-br"></div>

      <v-card-title class="rec-card-title">
        {{ postType === 'travel_plan' ? "I'm Traveling" : 'Share a Recommendation' }}
        <div class="rec-card-subtitle">
          {{ postType === 'travel_plan'
            ? "Tell the network where you're headed — fellow members can point you in the right direction."
            : "Tell the network what you do here — and what you wish were official." }}
        </div>
      </v-card-title>

      <v-card-text>
        <div class="mode-toggle">
          <button
            class="mode-btn"
            :class="{ active: postType === 'recommendation' }"
            @click="postType = 'recommendation'"
          >Recommend a Place</button>
          <button
            class="mode-btn"
            :class="{ active: postType === 'travel_plan' }"
            @click="postType = 'travel_plan'"
          >I'm Traveling</button>
        </div>

        <div class="rec-row">
          <div class="city-field-wrap">
            <v-text-field
              label="City"
              v-model="city"
              placeholder="e.g. New York"
              outlined
              dense
              dark
              color="amber"
              :readonly="!!lockCity"
              :hint="lockCity ? 'Posting to this chapter' : ''"
              :persistent-hint="!!lockCity"
              @focus="showCitySuggestions = true"
              @blur="onCityBlur"
            />
            <div v-if="showCitySuggestions && citySuggestions.length" class="city-suggestions">
              <p class="city-suggestions-label">Already a chapter — tap to use it</p>
              <button
                v-for="match in citySuggestions"
                :key="match"
                class="city-suggestion-item"
                @mousedown.prevent="selectCitySuggestion(match)"
              >{{ match }}</button>
            </div>
          </div>
          <v-text-field
            v-if="postType === 'recommendation'"
            label="Business name"
            v-model="businessName"
            placeholder="e.g. Sant Ambroeus"
            outlined
            dense
            dark
            color="amber"
          />
          <v-text-field
            v-else
            label="Dates (optional)"
            v-model="travelDates"
            placeholder="e.g. Aug 10-15"
            outlined
            dense
            dark
            color="amber"
          />
        </div>

        <div v-if="postType === 'travel_plan'" class="expiration-section">
          <p class="expiration-section-label">When should this disappear? (required)</p>
          <v-checkbox
            v-model="saveTravelPlanPermanently"
            label="Save this permanently (never auto-expire)"
            dark
            color="amber"
            density="compact"
            hide-details
          />
          <v-text-field
            v-if="!saveTravelPlanPermanently"
            type="datetime-local"
            label="Disappears at"
            v-model="travelEndDate"
            hint="Required unless you check 'save permanently' above — otherwise there's no way to say for sure whether the trip is still happening."
            persistent-hint
            outlined
            dense
            dark
            color="amber"
            class="expiration-date-field"
          />
        </div>
        <p v-if="postType === 'travel_plan' && !canSubmit && city.trim()" class="expiration-required-error">
          Set a disappear date, or check "Save this permanently."
        </p>

        <v-select
          v-if="postType === 'recommendation'"
          label="Category"
          v-model="category"
          :items="categories"
          outlined
          dense
          dark
          color="amber"
          item-color="amber"
        />

        <v-textarea
          v-if="postType === 'recommendation'"
          label="What do you do here, and why do you love it?"
          v-model="postText"
          auto-grow
          outlined
          dark
          color="amber"
          placeholder="e.g. My go-to for breakfast when I'm in town before 9am meetings — ask for the corner table."
        />
        <v-textarea
          v-else
          label="What are you looking for?"
          v-model="postText"
          auto-grow
          outlined
          dark
          color="amber"
          placeholder="e.g. First time in Chicago — would love dinner and coffee recommendations."
        />

        <v-file-input
          v-model="mediaFiles"
          accept="image/*,video/*"
          label="Add photos or videos"
          prepend-icon="mdi-camera"
          multiple
          outlined
          dense
          dark
          color="amber"
        />

        <!-- Media Previews -->
        <div v-if="mediaPreviews.length" class="media-grid">
          <div v-for="(item, idx) in mediaPreviews" :key="idx" class="media-thumb">
            <img v-if="item.type === 'image'" :src="item.url" />
            <video v-else :src="item.url" controls></video>
            <button class="media-remove-btn" @click="removeMediaFile(idx)">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
        </div>

        <div
          v-if="postType === 'recommendation'"
          class="arrangement-box"
          :class="{ active: wantsArrangement }"
          @click="wantsArrangement = !wantsArrangement"
        >
          <v-checkbox
            v-model="wantsArrangement"
            hide-details
            dark
            color="amber"
            @click.stop
          />
          <div>
            <p class="arrangement-title">I'd love an official concierge arrangement here</p>
            <p class="arrangement-sub">This tells us — and lets other members see — that you'd want priority access, perks, or a standing arrangement with this business.</p>
          </div>
        </div>

        <v-switch
          label="Visible to all reciprocal members"
          v-model="isPublic"
          hide-details
          dark
          color="amber"
          class="mt-2"
        />
        <p class="visibility-note">
          {{ isPublic
            ? (postType === 'travel_plan' ? 'Everyone in the network can see you\'re traveling and offer tips.' : 'Everyone in the network will see this recommendation.')
            : 'Only members in your circle will see this.' }}
        </p>

        <v-switch
          v-if="lockCity"
          label="Also show in Explore's main feed"
          v-model="alsoShareToExplore"
          hide-details
          dark
          color="amber"
          class="mt-2"
        />
        <p v-if="lockCity" class="visibility-note">
          {{ alsoShareToExplore
            ? 'This will also appear in the app-wide Explore feed, not just this chapter.'
            : `Off keeps this in the ${city || 'chapter'} chapter only.` }}
        </p>
      </v-card-text>

      <v-card-actions class="rec-actions">
        <button class="rec-btn-primary" :disabled="isUploading || !canSubmit" @click="submitPost">
          <div class="rec-btn-tick"></div>
          <span>{{ postType === 'travel_plan' ? 'Post Travel Plan' : 'Share Recommendation' }}</span>
          <div class="rec-btn-tick"></div>
        </button>
        <button class="rec-btn-text" @click="closeDialog">Cancel</button>
      </v-card-actions>
    </v-card>

    <v-overlay :value="isUploading">
      <v-progress-circular indeterminate size="64" color="amber" />
    </v-overlay>
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import {
  collection,
  doc,
  setDoc,
  addDoc,
  getDoc,
  getDocs,
  query,
  where,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore'
import { db } from '@/firebase'
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL
} from 'firebase/storage'
import { findMatchingCities } from '@/utils/city'

console.log("[DEBUG] PostCreateDialog.vue module loaded — build marker: has-lockCity-prop-v2")

const props = defineProps({
  modelValue: Boolean,
  user: Object,
  isPublic: Boolean,
  // When set (opening this dialog from a chapter page), the city field is
  // pre-filled and locked instead of freely typed — a chapter-originated
  // post should unambiguously belong to that chapter's city.
  lockCity: { type: String, default: '' },
  // Seeds groupOnly when the dialog opens. Chapters default this to true
  // ("post something just in the chapter" — see NewPostSheet.swift's
  // equivalent on iOS); Explore's own composer leaves it false.
  defaultGroupOnly: { type: Boolean, default: false },
  // Which of the two internal modes (mode-toggle above) to open on —
  // lets a "I'm Traveling" entry point default straight into that mode
  // instead of always landing on Recommend a Place.
  initialPostType: { type: String, default: 'recommendation' },
})

const emit = defineEmits(['update:modelValue', 'post-created'])

const categories = [
  'Breakfast',
  'Lunch',
  'Dinner',
  'Late Night',
  'Coffee & Drinks',
  'Hotel & Stay',
  'Experience & Culture',
  'Wellness',
  'Other',
]

const city = ref('')
const existingCities = ref([])
const showCitySuggestions = ref(false)
const businessName = ref('')
const category = ref('Dinner')
const postText = ref('')
const wantsArrangement = ref(false)
const mediaFiles = ref([])
const mediaPreviews = ref([]) // [{ url, type: 'image'|'video' }]
const isUploading = ref(false)
const dialogVisible = ref(props.modelValue)
const isPublic = ref(props.isPublic)
const postType = ref('recommendation') // 'recommendation' | 'travel_plan'
const travelDates = ref('')
const travelEndDate = ref('')
const saveTravelPlanPermanently = ref(false)
// Inverted the same way NewPostSheet.swift does it: this ref is what the
// UI shows and toggles ("Also show in Explore's main feed"); groupOnly
// itself (written to Firestore) is always !alsoShareToExplore.value.
const alsoShareToExplore = ref(!props.defaultGroupOnly)

const canSubmit = computed(() => {
  if (!city.value.trim()) return false
  if (postType.value === 'travel_plan') {
    return saveTravelPlanPermanently.value || !!travelEndDate.value
  }
  return businessName.value.trim() && category.value
})

const citySuggestions = computed(() => {
  if (!city.value.trim()) return []
  const matches = findMatchingCities(city.value, existingCities.value)
  // Don't show a suggestion that's already an exact match of what's typed
  return matches.filter(m => m.toLowerCase() !== city.value.trim().toLowerCase()).slice(0, 4)
})

const fetchExistingCities = async () => {
  try {
    const snap = await getDocs(collection(db, 'groups'))
    const cities = new Set()
    snap.forEach(d => {
      const c = d.data().city
      if (c) cities.add(c)
    })
    existingCities.value = [...cities]
  } catch (err) {
    console.error('Failed to load existing chapter cities:', err)
  }
}

const selectCitySuggestion = (match) => {
  city.value = match
  showCitySuggestions.value = false
}

const onCityBlur = () => {
  // Small delay so a suggestion click's mousedown can register before we hide the list
  setTimeout(() => { showCitySuggestions.value = false }, 150)
}

watch(() => props.modelValue, val => {
  console.log("[DEBUG] PostCreateDialog modelValue watcher fired:", val, "lockCity:", props.lockCity)
  dialogVisible.value = val
  if (val) {
    fetchExistingCities()
    if (props.lockCity) city.value = props.lockCity
    alsoShareToExplore.value = !props.defaultGroupOnly
    postType.value = props.initialPostType
  }
})

watch(dialogVisible, val => {
  emit('update:modelValue', val)
  if (!val) resetState()
})

watch(mediaFiles, files => {
  const list = files || []
  mediaPreviews.value = list.map(file => ({
    url: URL.createObjectURL(file),
    type: file.type.startsWith('image') ? 'image' : 'video',
  }))
})

const removeMediaFile = (idx) => {
  const files = [...mediaFiles.value]
  files.splice(idx, 1)
  mediaFiles.value = files
}

const resetState = () => {
  city.value = props.lockCity || ''
  businessName.value = ''
  category.value = 'Dinner'
  postText.value = ''
  wantsArrangement.value = false
  mediaFiles.value = []
  mediaPreviews.value = []
  isUploading.value = false
  postType.value = 'recommendation'
  travelDates.value = ''
  travelEndDate.value = ''
  saveTravelPlanPermanently.value = false
  alsoShareToExplore.value = !props.defaultGroupOnly
  showCitySuggestions.value = false
}

const closeDialog = () => {
  dialogVisible.value = false
}

function getTimeAgo(date) {
  if (!date) return ''
  const now = new Date()
  const diff = Math.floor((now - date) / 1000)
  if (diff < 60) return 'just now'
  if (diff < 3600) return `${Math.floor(diff / 60)} minute(s) ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)} hour(s) ago`
  if (diff < 172800) return 'yesterday'
  return date.toLocaleDateString()
}

const submitPost = async () => {
  if (!props.user || !canSubmit.value) return

  isUploading.value = true
  const id = crypto.randomUUID()
  const postRef = doc(db, 'explore', id)
  const storage = getStorage()

  const media = await Promise.all(
    mediaFiles.value.map(async (file, idx) => {
      const isImage = file.type.startsWith('image')
      const ext = isImage ? 'jpg' : 'mp4'
      const storagePath = `explore/${id}_${idx}_${Date.now()}.${ext}`
      const fileRef = storageRef(storage, storagePath)
      await uploadBytes(fileRef, file)
      const url = await getDownloadURL(fileRef)
      return { url, type: isImage ? 'image' : 'video' }
    })
  )

  const newPost = postType.value === 'travel_plan'
    ? {
        id,
        postType: 'travel_plan',
        city: city.value.trim(),
        travelDates: travelDates.value.trim(),
        travelEndDate: (!saveTravelPlanPermanently.value && travelEndDate.value) ? Timestamp.fromDate(new Date(travelEndDate.value)) : null,
        saved: saveTravelPlanPermanently.value,
        text: postText.value,
        fullName: props.user.fullName || 'Anonymous',
        userImageURL: props.user.image || '',
        media,
        isPublic: isPublic.value,
        groupOnly: !alsoShareToExplore.value,
        timestamp: serverTimestamp(),
        uid: props.user.uid,
      }
    : {
        id,
        postType: 'recommendation',
        city: city.value.trim(),
        businessName: businessName.value.trim(),
        category: category.value,
        text: postText.value,
        wantsArrangement: wantsArrangement.value,
        fullName: props.user.fullName || 'Anonymous',
        userImageURL: props.user.image || '',
        media,
        likes: [],
        arrangementInterest: wantsArrangement.value ? [props.user.uid] : [],
        isPublic: isPublic.value,
        groupOnly: !alsoShareToExplore.value,
        timestamp: serverTimestamp(),
        uid: props.user.uid,
      }

  await setDoc(postRef, newPost)

  if (postType.value === 'travel_plan' && isPublic.value) {
    await notifyRecommendersOfCity(city.value.trim(), id, props.user)
  }

  // ✅ Re-fetch the saved post to get resolved timestamp
  const savedSnap = await getDoc(postRef)
  const savedData = savedSnap.data()
  const resolvedTimestamp = savedData.timestamp?.toDate()

  emit('post-created', {
    ...savedData,
    id,
    arrangementCount: savedData.arrangementInterest?.length || 0,
    hasRequestedArrangement: !!savedData.arrangementInterest?.includes(props.user.uid),
    timestamp: resolvedTimestamp,
    timeAgo: getTimeAgo(resolvedTimestamp),
  })

  dialogVisible.value = false
}

// When a member posts that they're traveling somewhere, notify everyone who has
// already recommended a place in that city — they're the ones best placed to help.
const notifyRecommendersOfCity = async (cityName, travelPostId, user) => {
  try {
    const q = query(
      collection(db, 'explore'),
      where('city', '==', cityName),
      where('isPublic', '==', true)
    )
    const snap = await getDocs(q)
    const recommenderUids = new Set()
    snap.forEach(d => {
      const data = d.data()
      if (data.uid && data.uid !== user.uid && (data.postType || 'recommendation') !== 'travel_plan') {
        recommenderUids.add(data.uid)
      }
    })

    await Promise.all(
      [...recommenderUids].map(receiverId =>
        addDoc(collection(db, 'notifications'), {
          receiverId,
          senderId: user.uid,
          type: 'travel_plan',
          postId: travelPostId,
          postType: 'explore',
          message: `${user.fullName || 'A member'} is heading to ${cityName} — give them a recommendation`,
          timestamp: serverTimestamp(),
          read: false,
        })
      )
    )
  } catch (err) {
    console.error('Failed to notify recommenders of city:', err)
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cormorant:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Montserrat:wght@300;400;500;600&display=swap');

video { border-radius: 4px; }

/* ── MEDIA PREVIEW GRID ── */
.media-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin: 4px 0 16px; }
.media-thumb { position: relative; aspect-ratio: 1; border: 1px solid rgba(210,175,100,0.2); overflow: hidden; }
.media-thumb img, .media-thumb video { width: 100%; height: 100%; object-fit: cover; display: block; }
.media-remove-btn { position: absolute; top: 4px; right: 4px; width: 22px; height: 22px; border-radius: 50%; background: rgba(0,0,0,0.7); border: 1px solid rgba(210,175,100,0.3); color: rgba(237,232,223,0.8); display: flex; align-items: center; justify-content: center; cursor: pointer; transition: background 0.2s, color 0.2s; }
.media-remove-btn:hover { background: rgba(200,60,60,0.7); color: #fff; }
.media-remove-btn svg { width: 12px; height: 12px; }

/* ── DIALOG SHELL ── */
:deep(.rec-dialog) { box-shadow: none; }
.rec-card {
  font-family: 'Montserrat', sans-serif !important;
  background: #0d0c0a !important;
  border: 1px solid rgba(210,175,100,0.22) !important;
  position: relative;
  overflow: visible !important;
}
.rec-frame-tl, .rec-frame-br { position: absolute; width: 16px; height: 16px; pointer-events: none; z-index: 2; }
.rec-frame-tl { top: -1px; left: -1px; border-top: 1px solid rgba(210,175,100,0.6); border-left: 1px solid rgba(210,175,100,0.6); }
.rec-frame-br { bottom: -1px; right: -1px; border-bottom: 1px solid rgba(210,175,100,0.6); border-right: 1px solid rgba(210,175,100,0.6); }

.rec-card-title {
  display: flex; flex-direction: column; align-items: flex-start;
  font-family: 'Cormorant', serif !important; font-size: 30px !important; font-weight: 400 !important;
  color: #edeae2 !important;
  padding: 32px 32px 4px !important;
}
.rec-card-subtitle {
  font-family: 'Montserrat', sans-serif; font-size: 12px; font-weight: 400;
  color: rgba(237,232,223,0.35); margin-top: 8px; letter-spacing: 0.2px;
}

:deep(.v-card__text) { padding: 20px 32px 4px !important; }

.rec-row { display: flex; gap: 12px; }
.rec-row > * { flex: 1; }
.expiration-section { margin-top: 4px; margin-bottom: 16px; padding: 14px 16px; border: 1px solid rgba(210,175,100,0.15); background: rgba(210,175,100,0.03); }
.expiration-section-label { font-family: 'Montserrat', sans-serif; font-size: 11px; font-weight: 600; letter-spacing: 0.5px; color: rgba(210,175,100,0.6); margin: 0 0 10px; }
.expiration-date-field { margin-top: 12px; max-width: 320px; }
.expiration-required-error { font-family: 'Montserrat', sans-serif; font-size: 12px; color: rgba(220,100,90,0.85); margin: -8px 0 16px; }

.city-field-wrap { position: relative; flex: 1; }
.city-suggestions { position: absolute; top: calc(100% - 4px); left: 0; right: 0; z-index: 20; background: #0f0e0c; border: 1px solid rgba(210,175,100,0.3); padding: 6px 0; }
.city-suggestions-label { font-size: 8px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; color: rgba(210,175,100,0.4); padding: 6px 14px 4px; }
.city-suggestion-item { display: block; width: 100%; text-align: left; background: none; border: none; cursor: pointer; padding: 9px 14px; font-family: 'Montserrat', sans-serif; font-size: 13px; font-weight: 400; color: rgba(237,232,223,0.75); transition: background 0.15s, color 0.15s; }
.city-suggestion-item:hover { background: rgba(210,175,100,0.1); color: rgba(210,175,100,0.95); }

.mode-toggle { display: flex; border: 1px solid rgba(210,175,100,0.25); margin-bottom: 20px; }
.mode-btn { flex: 1; background: transparent; border: none; padding: 12px 10px; cursor: pointer; font-family: 'Montserrat', sans-serif; font-size: 10px; font-weight: 600; letter-spacing: 1.2px; text-transform: uppercase; color: rgba(237,232,223,0.35); transition: background 0.2s, color 0.2s; }
.mode-btn + .mode-btn { border-left: 1px solid rgba(210,175,100,0.2); }
.mode-btn.active { background: rgba(210,175,100,0.1); color: rgba(210,175,100,0.9); }
.mode-btn:hover:not(.active) { color: rgba(237,232,223,0.6); }

/* ── VUETIFY FIELD OVERRIDES ── */
:deep(.v-text-field .v-label),
:deep(.v-textarea .v-label),
:deep(.v-select .v-label),
:deep(.v-file-input .v-label) {
  color: rgba(237,232,223,0.35) !important;
  font-family: 'Montserrat', sans-serif;
  font-size: 13px;
}
:deep(.v-text-field input),
:deep(.v-textarea textarea),
:deep(.v-select .v-select__selection),
:deep(.v-file-input .v-file-input__text) {
  color: #edeae2 !important;
  font-family: 'Montserrat', sans-serif;
}
:deep(.v-input--is-focused .v-label) { color: rgba(210,175,100,0.8) !important; }
:deep(fieldset) { border-color: rgba(210,175,100,0.22) !important; border-radius: 2px !important; }
:deep(.v-input--is-focused fieldset) { border-color: rgba(210,175,100,0.6) !important; }
:deep(.v-text-field--outlined:hover fieldset),
:deep(.v-textarea--outlined:hover fieldset),
:deep(.v-select--outlined:hover fieldset) { border-color: rgba(210,175,100,0.4) !important; }
:deep(.v-icon) { color: rgba(210,175,100,0.45) !important; }
:deep(.v-select__selections),
:deep(.v-file-input__text) { font-size: 14px; }
:deep(.v-menu__content) { background: #0f0e0c !important; border: 1px solid rgba(210,175,100,0.2) !important; }
:deep(.v-list-item__title) { color: rgba(237,232,223,0.7) !important; font-family: 'Montserrat', sans-serif; }
:deep(.v-list-item--active .v-list-item__title) { color: rgba(210,175,100,0.9) !important; }
:deep(.v-list) { background: #0f0e0c !important; }

/* ── SWITCH / CHECKBOX ── */
:deep(.v-input--switch .v-label),
:deep(.v-input--checkbox .v-label) { color: rgba(237,232,223,0.55) !important; font-size: 13px; }

.arrangement-box {
  display: flex; align-items: flex-start; gap: 4px;
  border: 1px solid rgba(210,175,100,0.28);
  border-left: 3px solid rgba(210,175,100,0.28);
  background: rgba(210,175,100,0.05);
  border-radius: 2px;
  padding: 8px 16px 8px 4px;
  margin: 6px 0 18px;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s;
}
/* Previously only a faint opacity bump (0.05→0.1 background,
   0.28→0.45 border) — easy to miss entirely, which is exactly the bug:
   this defaults off, but was hard to tell apart from "on" at a glance. */
.arrangement-box.active {
  background: rgba(210,175,100,0.16);
  border-color: rgba(210,175,100,0.7);
  border-left-color: #d2af64;
}
.arrangement-title { font-size: 13px; font-weight: 600; color: #edeae2; margin-bottom: 3px; }
.arrangement-box.active .arrangement-title { color: #d2af64; font-weight: 700; }
.arrangement-sub { font-size: 11.5px; line-height: 1.55; color: rgba(237,232,223,0.4); }
.visibility-note { font-size: 11px; color: rgba(237,232,223,0.3); margin: -8px 0 8px 2px; }

/* ── ACTIONS ── */
.rec-actions { padding: 12px 32px 32px !important; gap: 14px; }
.rec-btn-primary {
  display: flex; align-items: center; gap: 12px;
  font-family: 'Montserrat', sans-serif; font-size: 9.5px; font-weight: 600; letter-spacing: 2.5px; text-transform: uppercase;
  color: rgba(210,175,100,0.9);
  background: rgba(210,175,100,0.08);
  border: 1px solid rgba(210,175,100,0.5);
  padding: 13px 22px;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s, color 0.2s, opacity 0.2s;
}
.rec-btn-primary:hover:not(:disabled) { background: rgba(210,175,100,0.16); border-color: rgba(210,175,100,0.75); }
.rec-btn-primary:disabled { opacity: 0.35; cursor: not-allowed; }
.rec-btn-tick { width: 14px; height: 1px; background: rgba(210,175,100,0.4); flex-shrink: 0; }
.rec-btn-text {
  font-family: 'Montserrat', sans-serif; font-size: 9.5px; font-weight: 600; letter-spacing: 2.5px; text-transform: uppercase;
  color: rgba(237,232,223,0.3);
  background: none; border: none; cursor: pointer; padding: 13px 10px;
  transition: color 0.2s;
}
.rec-btn-text:hover { color: rgba(237,232,223,0.6); }
</style>